import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Eye, EyeOff, LockKeyhole, Save, ShieldCheck, UserCircle } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../hooks/useNotification';
import { changeMyPassword } from '../../services/api';

const PASSWORD_AUTH_PROVIDER = 'NORMAL';

type PasswordField = 'current' | 'next' | 'confirm';

export const AccountSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const notification = useNotification();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [visiblePasswordFields, setVisiblePasswordFields] = useState<
    Record<PasswordField, boolean>
  >({
    current: false,
    next: false,
    confirm: false,
  });

  const displayName = user?.fullName || user?.userName || user?.email || 'Admin';
  const canChangePassword =
    Boolean(user) && (!user?.authProvider || user.authProvider === PASSWORD_AUTH_PROVIDER);

  const passwordValidationError = useMemo(() => {
    if (!newPassword) return '';
    if (newPassword.length < 8) return 'New password must be at least 8 characters.';
    if (newPassword.length > 128) return 'New password must be less than 128 characters.';
    if (!/\d/.test(newPassword)) return 'New password must contain at least one number.';
    if (!/[a-zA-Z]/.test(newPassword)) return 'New password must contain at least one letter.';
    if (currentPassword && newPassword === currentPassword) {
      return 'New password must be different from the current password.';
    }
    return '';
  }, [currentPassword, newPassword]);

  const confirmPasswordError =
    confirmPassword && newPassword !== confirmPassword
      ? 'Password confirmation does not match.'
      : '';

  const canSubmitPassword =
    Boolean(currentPassword && newPassword && confirmPassword) &&
    !passwordValidationError &&
    !confirmPasswordError;

  const togglePasswordVisibility = (field: PasswordField) => {
    setVisiblePasswordFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const renderPasswordVisibilityButton = (field: PasswordField, label: string) => (
    <InputAdornment position="end">
      <Tooltip title={label}>
        <IconButton
          type="button"
          edge="end"
          onClick={() => togglePasswordVisibility(field)}
          aria-label={label}
          disabled={isChangingPassword}
        >
          {visiblePasswordFields[field] ? <EyeOff size={18} /> : <Eye size={18} />}
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canSubmitPassword) {
      notification.error(
        passwordValidationError || confirmPasswordError || 'Please complete all password fields.'
      );
      return;
    }

    setIsChangingPassword(true);
    const result = await changeMyPassword({
      currentPassword,
      newPassword,
    });
    setIsChangingPassword(false);

    if (!result.success) {
      notification.error(result.error || 'Failed to change password.');
      return;
    }

    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    notification.success('Password changed successfully.');
  };

  return (
    <Box className="h-full bg-slate-50 p-8 dark:bg-slate-900">
      <Box className="mb-6">
        <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
          Account Settings
        </Typography>
        <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
          Manage your administrator account security.
        </Typography>
      </Box>

      <Box className="grid max-w-5xl gap-6 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
        <Paper
          elevation={0}
          className="rounded-lg bg-white p-6 shadow-sm dark:bg-slate-800"
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <Box className="mb-5 flex items-start gap-3">
            <Box className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 dark:bg-sky-900/30">
              <UserCircle className="text-sky-600 dark:text-sky-400" size={22} />
            </Box>
            <Box className="min-w-0">
              <Typography
                variant="subtitle1"
                className="font-semibold text-gray-900 dark:text-white"
              >
                Profile
              </Typography>
              <Typography variant="body2" className="mt-1 text-gray-500 dark:text-slate-400">
                Current signed-in admin account.
              </Typography>
            </Box>
          </Box>

          <Box className="space-y-3">
            <Box>
              <Typography
                variant="caption"
                className="font-semibold uppercase text-gray-500 dark:text-slate-400"
              >
                Name
              </Typography>
              <Typography
                variant="body1"
                className="mt-1 break-words font-semibold text-gray-900 dark:text-slate-100"
              >
                {displayName}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                className="font-semibold uppercase text-gray-500 dark:text-slate-400"
              >
                Email
              </Typography>
              <Typography
                variant="body2"
                className="mt-1 break-words text-gray-700 dark:text-slate-300"
              >
                {user?.email}
              </Typography>
            </Box>
            <Box className="flex flex-wrap items-center gap-2 pt-1">
              <Chip
                size="small"
                icon={<ShieldCheck size={14} />}
                label={user?.role || 'admin'}
                className="font-semibold"
                color="primary"
                variant="outlined"
              />
              {user?.authProvider && (
                <Chip size="small" label={user.authProvider} variant="outlined" />
              )}
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          className="rounded-lg bg-white p-6 shadow-sm dark:bg-slate-800"
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <Box className="mb-5 flex items-start gap-3">
            <Box className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <LockKeyhole className="text-amber-600 dark:text-amber-400" size={20} />
            </Box>
            <Box className="min-w-0">
              <Typography
                variant="subtitle1"
                className="font-semibold text-gray-900 dark:text-white"
              >
                Change Password
              </Typography>
              <Typography variant="body2" className="mt-1 text-gray-500 dark:text-slate-400">
                Update the password used to sign in to the admin site.
              </Typography>
            </Box>
          </Box>

          {canChangePassword ? (
            <Box component="form" onSubmit={handlePasswordSubmit} className="space-y-4">
              <TextField
                fullWidth
                label="Current password"
                type={visiblePasswordFields.current ? 'text' : 'password'}
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                disabled={isChangingPassword}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: renderPasswordVisibilityButton(
                    'current',
                    visiblePasswordFields.current
                      ? 'Hide current password'
                      : 'Show current password'
                  ),
                }}
              />
              <TextField
                fullWidth
                label="New password"
                type={visiblePasswordFields.next ? 'text' : 'password'}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                disabled={isChangingPassword}
                autoComplete="new-password"
                error={Boolean(passwordValidationError)}
                helperText={
                  passwordValidationError ||
                  'Use 8-128 characters with at least one letter and one number.'
                }
                InputProps={{
                  endAdornment: renderPasswordVisibilityButton(
                    'next',
                    visiblePasswordFields.next ? 'Hide new password' : 'Show new password'
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirm new password"
                type={visiblePasswordFields.confirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                disabled={isChangingPassword}
                autoComplete="new-password"
                error={Boolean(confirmPasswordError)}
                helperText={confirmPasswordError || ' '}
                InputProps={{
                  endAdornment: renderPasswordVisibilityButton(
                    'confirm',
                    visiblePasswordFields.confirm
                      ? 'Hide password confirmation'
                      : 'Show password confirmation'
                  ),
                }}
              />
              <Box className="flex justify-end">
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save size={18} />}
                  disabled={!canSubmitPassword || isChangingPassword}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {isChangingPassword ? 'Saving...' : 'Save Password'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-900/20">
              <Typography variant="body2" className="text-amber-800 dark:text-amber-100">
                Password changes are only available for accounts created with email and password.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
